import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Dropdown, {
  DropdownItemProps,
  DropdownOption,
  DropdownOptionGroup,
} from 'src/components/Dropdown/Dropdown'
import TestId from 'src/components/TestId/TestId'
import { getLocaleInfo, LocaleType } from 'src/localization/locales'
import { translateSingleString } from 'src/localization/Trns'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

export interface LocaleOptionDetails {
  locale: LocaleType
  nativeTranslatedDisplayName: string
  localeTranslatedDisplayName: string
}

export type LocaleOption = DropdownOption<LocaleOptionDetails>

const LocaleDropdownItem: React.FC<DropdownItemProps<LocaleOptionDetails>> = (
  props,
): React.ReactElement => {
  return (
    <TestId testId={props.value}>
      <div
        className={props.className}
        css={css`
          display: flex;
          align-items: center;
          flex-grow: 2;
          height: ${props.isRoot ? '100%' : '70px'};
        `}
      >
        {props.details !== undefined && (
          <div
            css={css`
              display: flex;
              align-items: left;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  props.isRoot ? 16 : 14,
                  props.isSelected
                    ? themeConstants.colors.cta
                    : themeConstants.colors.font_primary,
                  props.isSelected || props.isRoot
                    ? styleConstants.fontFamilies.medium
                    : styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {props.details.nativeTranslatedDisplayName}
            </span>
            <span
              css={css`
                ${getFontCss(
                  props.isRoot ? 14 : 12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {props.details.localeTranslatedDisplayName}
            </span>
          </div>
        )}
      </div>
    </TestId>
  )
}

const LocaleOptionFilter = (option: LocaleOption, val: string): boolean =>
  !!option.details?.nativeTranslatedDisplayName
    .toLowerCase()
    .includes(val.toLowerCase()) ||
  !!option.details?.localeTranslatedDisplayName
    .toLowerCase()
    .includes(val.toLowerCase())

const getLocaleOptionFilterText = (option: LocaleOption): string =>
  option.value || ''

interface LocaleDropdownProps {
  className?: string
  selectedValue?: string
  chooseOption: (val: string) => void
  errors?: Array<React.ReactElement | string>
}

const LocaleDropdown: React.FC<LocaleDropdownProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Default [] preserves the legacy redux initial-state render (empty
  // dropdown while pending) — the config reducer had no loading/error flags.
  const { data: locales = [] } = useQuery({
    ...sessionConfigQueries.byLocale(locale),
    select: (config) => config.supportedLocales,
  })
  const optionGroups: Array<DropdownOptionGroup<LocaleOption>> = useMemo(
    () => [
      {
        options: locales.map((curLocale) => {
          const localeInfo = getLocaleInfo(curLocale)
          return {
            value: localeInfo.iso,
            details: {
              locale: localeInfo,
              nativeTranslatedDisplayName: translateSingleString(
                localeInfo.iso,
                localeInfo.displayName,
              ),
              localeTranslatedDisplayName: translateSingleString(
                locale,
                localeInfo.displayName,
              ),
            },
          }
        }),
      },
    ],
    [locales],
  )

  return (
    <Dropdown
      className={props.className}
      DropdownItem={LocaleDropdownItem}
      optionGroups={optionGroups}
      isMulti={false}
      selectedValues={
        props.selectedValue !== undefined
          ? new Set([props.selectedValue])
          : new Set([])
      }
      chooseOption={props.chooseOption}
      errors={props.errors}
      filter={LocaleOptionFilter}
      getFilterText={getLocaleOptionFilterText}
      validateNewOption={undefined}
    />
  )
}

export default LocaleDropdown
